import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { useApp } from "../context/AppContext";
import { api } from "../utils/api";
import PostCard from "../components/PostCard";
import PageMeta from "../components/PageMeta";
import ModernMapTiles from "../components/ModernMapTiles";
import { usePaymentReturnBanner } from "../utils/paymentReturn";

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

const destinationIcon = () => L.divIcon({
  html: `<div style="width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#0a0a0a;border:3px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.3)"></div>`,
  className: "",
  iconSize: [34, 34],
  iconAnchor: [17, 32],
});

const BellIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

// Page de détail d'une publication — sert notamment de destination aux invitations
// ("X t'invite à ...") et aux liens partagés, qui n'avaient jusqu'ici nulle part où mener.
// Pour les passions/powers : participants (X/Y ou Complet), liste d'attente, itinéraire.
export default function Publication() {
  const { lang, user } = useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [waitlistBusy, setWaitlistBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const load = () => {
    if (!id) return;
    api.get(`/publications/${id}`).then(data => {
      if (!data?.publication) { setNotFound(true); return; }
      setPost(data.publication);
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);
  usePaymentReturnBanner();

  const toggleReaction = (p: any, type: "interested" | "top" | "envieux") => {
    api.post(`/publications/${p.id}/react`, { type }).then(data => {
      if (data?.action) {
        setPost((prev: any) => prev && prev.id === p.id ? {
          ...prev,
          tops_count: data.tops_count ?? prev.tops_count,
          envieux_count: data.envieux_count ?? prev.envieux_count,
          interested_count: data.interested_count ?? prev.interested_count,
          my_reactions: data.action === "added"
            ? [...(prev.my_reactions || []), type]
            : (prev.my_reactions || []).filter((r: string) => r !== type),
        } : prev);
      }
    }).catch(() => {});
  };

  const participate = (p: any) => {
    if (p.my_participation_status) return;
    const success_url = `${window.location.origin}${window.location.pathname}?payment=success`;
    const cancel_url = `${window.location.origin}${window.location.pathname}?payment=cancelled`;
    api.post(`/publications/${p.id}/participate`, { success_url, cancel_url, lang }).then(data => {
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else if (data?.status) {
        setPost((prev: any) => prev && prev.id === p.id ? { ...prev, my_participation_status: data.status } : prev);
      } else if (data?.error) {
        alert(data.error);
      }
    }).catch(() => {});
  };

  const leaveActivity = () => {
    if (!post || leaving) return;
    if (!window.confirm("Quitter cette activité ? Ta place sera libérée.")) return;
    setLeaving(true);
    api.delete(`/publications/${post.id}/participate`).then(data => {
      if (data?.message) {
        setPost((prev: any) => prev ? { ...prev, my_participation_status: null, current_places: Math.max(0, prev.current_places - 1) } : prev);
      } else if (data?.error) alert(data.error);
    }).catch(() => {}).finally(() => setLeaving(false));
  };

  const toggleWaitlist = () => {
    if (!post || waitlistBusy) return;
    setWaitlistBusy(true);
    const call = post.on_waitlist ? api.delete(`/publications/${post.id}/waitlist`) : api.post(`/publications/${post.id}/waitlist`, {});
    call.then(data => {
      if (data?.error) { alert(data.error); return; }
      setPost((prev: any) => prev ? { ...prev, on_waitlist: !prev.on_waitlist, waitlist_count: Number(prev.waitlist_count || 0) + (prev.on_waitlist ? -1 : 1) } : prev);
    }).catch(() => {}).finally(() => setWaitlistBusy(false));
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>Chargement...</div>;
  if (notFound || !post) return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>Publication introuvable.</div>;

  const isBookable = post.type === "passion" || post.type === "power";
  const isFull = isBookable && post.max_places > 0 && post.current_places >= post.max_places;
  const isOwner = user?.id === post.user_id;
  const confirmedParticipants = post.confirmed_participants || [];
  const hasLocation = post.latitude && post.longitude;
  const directionsUrl = hasLocation ? `https://www.google.com/maps/dir/?api=1&destination=${post.latitude},${post.longitude}` : null;

  return (
    <>
    <PageMeta title={post.title || "Publication"} description={post.description} image={post.cover_url} />
    <div style={{ maxWidth: 600, margin: "0 auto", padding: 20 }}>
      <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", color: "#666", fontSize: 14, fontWeight: 600, padding: 0, marginBottom: 16 }}>← Retour</button>
      <PostCard post={post} lang={lang} user={user} navigate={navigate} toggleReaction={toggleReaction} participate={participate} />

      {isBookable && post.max_places > 0 && (
        <div style={{ backgroundColor: "white", borderRadius: 16, padding: 18, marginBottom: 16, border: "1px solid #F0F0F0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: confirmedParticipants.length > 0 ? 14 : 0 }}>
            <span style={{ fontSize: 15, fontWeight: 700 }}>Participants</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: isFull ? "#c0392b" : "#0a0a0a" }}>
              {isFull ? "Complet" : `${post.current_places}/${post.max_places}`}
            </span>
          </div>

          {confirmedParticipants.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 12 }}>
              {confirmedParticipants.map((pt: any) => (
                <div key={pt.id} onClick={() => navigate(`/profile/${pt.id}`)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 56, cursor: "pointer" }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", backgroundColor: "#0a0a0a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 13, fontWeight: 700 }}>
                    {pt.avatar_url ? <img src={pt.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(pt.name)}
                  </div>
                  <span style={{ fontSize: 11, color: "#666", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 56 }}>{pt.name.split(" ")[0]}</span>
                </div>
              ))}
            </div>
          )}

          {isOwner ? null : post.my_participation_status === "confirmed" ? (
            <button onClick={leaveActivity} disabled={leaving} style={{ width: "100%", marginTop: 16, padding: "12px", borderRadius: 12, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#0a0a0a", fontSize: 13.5, fontWeight: 600, cursor: "pointer", opacity: leaving ? 0.6 : 1 }}>
              Quitter cette activité
            </button>
          ) : isFull ? (
            <button onClick={toggleWaitlist} disabled={waitlistBusy} style={{ width: "100%", marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px", borderRadius: 12, border: "none", backgroundColor: post.on_waitlist ? "#F0F0F0" : "#0a0a0a", color: post.on_waitlist ? "#333" : "white", fontSize: 13.5, fontWeight: 700, cursor: "pointer", opacity: waitlistBusy ? 0.6 : 1 }}>
              <BellIcon /> {post.on_waitlist ? "Tu seras prévenu, annuler" : "Me prévenir si une place se libère"}
            </button>
          ) : null}
        </div>
      )}

      {isBookable && hasLocation && (
        // Toute la carte ouvre l'itinéraire Google Maps au clic — plus besoin du lien
        // "pin rouge" séparé sous la carte, qui faisait doublon avec le repère noir.
        <div onClick={() => window.open(directionsUrl!, "_blank", "noopener,noreferrer")} style={{ backgroundColor: "white", borderRadius: 16, padding: 18, marginBottom: 16, border: "1px solid #F0F0F0", cursor: "pointer" }}>
          <span style={{ fontSize: 15, fontWeight: 700, display: "block", marginBottom: 12 }}>Itinéraire</span>
          <div style={{ height: 180, borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
            <MapContainer center={[post.latitude, post.longitude]} zoom={13} style={{ width: "100%", height: "100%" }} zoomControl={false} dragging={false} scrollWheelZoom={false} doubleClickZoom={false} touchZoom={false} attributionControl={false}>
              <ModernMapTiles />
              <Marker position={[post.latitude, post.longitude]} icon={destinationIcon()} />
            </MapContainer>
          </div>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "#0a0a0a" }}>
            {post.location_name || post.venue_name || "Itinéraire"}
          </span>
        </div>
      )}
    </div>
    </>
  );
}
